import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon, PulseIcon } from '@phosphor-icons/react';

import { useAgentSessions } from '@/api-models/agent-sessions';
import { useRepositories } from '@/api-models/repositories';
import { teamPath, useSpace } from '@/contexts/space-context';
import { LocationBar } from '@/shared/shell/LocationBar';
import { ViewBar } from '@/shared/shell/ViewBar';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/button/Button';

import {
  makeRepoLabel,
  SESSION_FILTER_FIELDS,
  SESSION_GROUP_FIELDS,
  SESSION_SORT_FIELDS,
  useSessionControls,
} from '../components/session-controls';
import { SessionsList } from '../components/SessionsList';

/** Chat-first sessions for the active team. Picking one opens its full-page thread. */
export function AgentSessionsPage() {
  const { teamId } = useSpace();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sessions, isLoading, mutate } = useAgentSessions(teamId);
  const { repositories } = useRepositories(teamId);
  const controls = useSessionControls(sessions, repositories);
  const repoLabel = makeRepoLabel(repositories);

  // Legacy `?new=1` (old sidebar / bookmarks) now lands on the dedicated page.
  useEffect(() => {
    if (searchParams.get('new') === null) return;
    navigate(teamPath(teamId, '/sessions/new'), { replace: true });
  }, [searchParams, navigate, teamId]);

  return (
    <div className="flex h-full flex-col leading-tight">
      <LocationBar
        action={
          <Button
            variant="primary"
            size={8}
            onClick={() => navigate(teamPath(teamId, '/sessions/new'))}
            iconLeft={<PlusIcon size={14} weight="bold" />}
          >
            New session
          </Button>
        }
      >
        <PulseIcon size={17} weight="bold" className="shrink-0 text-brand-text" />
        <span className="text-sm font-semibold text-fg">Sessions</span>
        <Badge>{controls.filteredCount}</Badge>
      </LocationBar>

      <ViewBar
        filterFields={SESSION_FILTER_FIELDS}
        groupFields={SESSION_GROUP_FIELDS}
        sortFields={SESSION_SORT_FIELDS}
        filters={controls.filters}
        groupField={controls.groupField}
        sort={controls.sort}
        showEmptyGroups={controls.showEmptyGroups}
        fieldOptions={controls.fieldOptions}
        onToggleFilterValue={controls.toggleFilterValue}
        onChangeFilter={controls.changeFilter}
        onRemoveFilter={controls.removeFilter}
        onGroupChange={controls.setGroupField}
        onSortChange={controls.setSort}
        onToggleEmptyGroups={controls.toggleEmptyGroups}
        displayToggles={[
          {
            label: 'Show archived sessions',
            checked: controls.showArchived,
            onToggle: controls.toggleArchived,
          },
        ]}
        search={controls.search}
        onSearchChange={controls.setSearch}
        searchPlaceholder="Search sessions"
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <SessionsList
          groups={controls.groups}
          grouped={controls.grouped}
          isLoading={isLoading && sessions.length === 0}
          isFiltered={sessions.length > 0}
          onOpenSession={(id) => navigate(teamPath(teamId, `/sessions/${id}`))}
          repoLabel={repoLabel}
          onSessionsChanged={() => void mutate()}
        />
      </div>
    </div>
  );
}
